// One card for a family's entry in an event, the same on the parent
// dashboard and on the event page (MC2 ecbc86f7, Devin 2026-10-18: "blend
// the best of each"). Plain functions so both screens build it alike.
import { useEffect, useState } from "react";
import { formatDate } from "../../helper/formatDate";
import { inLateWindow } from "../../helper/lateEntries";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { summarySections } from "../../Parent/Register/registrationModel";

const API = process.env.REACT_APP_BASE_URL;
const endOfDay = (d) => {
  const e = formatDate(d);
  e.setHours(23, 59, 59, 999);
  return e;
};

// Number cards (and dancer names and numbers on the competitor list) open
// when the organiser marks merges and splits done; the counts are always
// open. This replaced the organiser's print-card date (Devin 2026-10-18).
// From the feis day on they are open regardless (Devin 2026-10-22: "day of
// for sure on"; the server's competitor list follows the same rule).
export const numberCardsReady = (event, now = new Date()) => !!event?.entriesFinal || dayHasCome(event, "feis", now);

// The event day has come (a feis then leads with results; no more changes).
export const dayHasCome = (event, kind = "feis", now = new Date()) => {
  const d = kind === "grade" ? event?.exam_date : event?.feis_date;
  return !!d && now >= formatDate(d);
};
export const dayHasPassed = (event, kind = "feis", now = new Date()) => {
  const d = kind === "grade" ? event?.exam_date : event?.feis_date;
  return !!d && now > endOfDay(d);
};

// Registration open today (opening..closing day, the family's own dates),
// or closed but still taking late entries (helper/lateEntries.js).
export const registrationOpen = (event, now = new Date()) => {
  if (!event?.closing_date) return false;
  if (event.opening_date && now < formatDate(event.opening_date)) return false;
  return now <= endOfDay(event.closing_date) || inLateWindow(event, now);
};

// A competition's age group for its label ("Reel U11"), from the syllabus
// ages it takes: the oldest of a range ("U12", "U13" -> "U13"), "13 & over"
// when it runs up to the top ("U14" .. "22 AND OVER"), "Adult" for adults
// only. Nothing when the name already says it ("Phantom Future Champ U9").
const ageNumber = (a) => (/and over/i.test(a) ? 99 : Number(String(a).replace(/\D/g, "")) || null);
export const ageLabel = (comp) => {
  const nums = (comp?.ages || []).map(ageNumber).filter(Boolean).sort((a, b) => a - b);
  if (!nums.length) return "";
  if (/\bu\d|\d\s*(&|and)\s*(over|older|under)|adult|over \d/i.test(String(comp?.description || ""))) return "";
  const top = nums[nums.length - 1];
  if (top !== 99) return `U${top}`;
  if (nums.length === 1) return "Adult";
  return `${nums[0] - 1} & over`;
};

// A family's registrations for one event as the card's dancers: each with
// its dances by level (a 2nd chance under its dance) and the competition
// codes the counts are read by. Grade exams list the grades when the
// registration carries them.
export const entriesFrom = (registrations, { kind = "feis", event, now = new Date(), onChange, onPrint } = {}) => {
  const open = numberCardsReady(event, now);
  const canChange = !dayHasPassed(event, kind, now) && !(kind === "feis" && dayHasCome(event, kind, now));
  // the same order on every screen: as they were entered
  const ordered = [...(registrations || [])].sort((a, b) => String(a?._id || "").localeCompare(String(b?._id || "")));
  return ordered.map((reg) => {
    const name = [reg?.dancerData?.fname, reg?.dancerData?.lname].filter(Boolean).join(" ");
    const active = reg?.dancerData?.status !== false && reg?.dancerStatus !== false;
    if (kind === "grade") {
      const grades = reg?.gradeDetails || [];
      return {
        id: reg._id,
        name,
        active,
        count: grades.length ? (grades.length === 1 ? "1 grade" : `${grades.length} grades`) : "",
        sections: grades.length ? [{ key: "grades", title: "Grades", rows: grades.map((g, i) => ({ id: g._id || String(i), name: g.grade_name })) }] : [],
        onChange: canChange && active && onChange ? () => onChange(reg) : null,
        print: null,
      };
    }
    const comps = reg?.competitionDetails || reg?.entries || [];
    const index = new Map(comps.map((c) => [String(c._id), c]));
    return {
      id: reg._id,
      name,
      active,
      count: comps.length === 1 ? "1 dance" : `${comps.length} dances`,
      sections: summarySections([...index.keys()], index, reg?.entryGroups).map((sec) => ({
        key: sec.key,
        title: sec.title,
        rows: sec.rows.map((r) => ({ ...r, code: index.get(r.id)?.code, age: r.sub ? "" : ageLabel(index.get(r.id)) })),
      })),
      onChange: canChange && active && onChange ? () => onChange(reg) : null,
      print: onPrint ? { available: open, label: "Number card", onClick: () => onPrint(reg) } : null,
    };
  });
};

// The card's buttons, in order: Competitors (feis), Syllabus,
// Schedule (once posted), then the main action - "Enter <name>" / "Enter
// another dancer" while registration is open and a family dancer is not
// entered yet, or "View results" from the feis day.
export const cardActions = ({ event, kind = "feis", notEntered = [], now = new Date(), go = {} }) => {
  if (!event) return [];
  const acts = [];
  if (kind === "feis" && go.counts) acts.push({ key: "counts", icon: "bar_chart", label: "Competitors", onClick: go.counts });
  if (event.syllabus_file) acts.push({ key: "syllabus", icon: "description", label: "Syllabus", onClick: () => go.open?.(event.syllabus_file) });
  if (event.schedulePdfUrl) acts.push({ key: "schedule", icon: "calendar_month", label: "Schedule", onClick: () => go.open?.(event.schedulePdfUrl) });
  if (kind === "feis" && dayHasCome(event, kind, now) && go.results) {
    // the feis day can still take late entries (MC2 5a45d7e3): results stay
    // the main action, entering another dancer sits beside it
    if (registrationOpen(event, now) && notEntered.length > 0 && go.add) {
      acts.push({
        key: "add",
        icon: "person_add",
        label: notEntered.length === 1 && notEntered[0]?.fname ? `Enter ${notEntered[0].fname}` : "Enter another dancer",
        onClick: go.add,
      });
    }
    acts.push({ key: "results", icon: "emoji_events", label: "View results", primary: true, onClick: go.results });
  } else if (registrationOpen(event, now) && notEntered.length > 0 && go.add) {
    acts.push({
      key: "add",
      icon: "person_add",
      label: notEntered.length === 1 && notEntered[0]?.fname ? `Enter ${notEntered[0].fname}` : "Enter another dancer",
      primary: true,
      onClick: go.add,
    });
  }
  return acts;
};

// The family's dancers not yet entered in an event (active ones only).
export const notEnteredIn = (family, registrations) => {
  const entered = new Set((registrations || []).map((r) => String(r?.dancer_id || r?.dancerData?._id)));
  return (family || []).filter((d) => d?.status && !entered.has(String(d._id)));
};

// How many dancers are in each competition, from the competitor list.
export const countsByCode = (dancers) => {
  const m = new Map();
  for (const d of Array.isArray(dancers) ? dancers : []) {
    for (const c of d?.competitonData || []) if (c?.code) m.set(c.code, (m.get(c.code) || 0) + 1);
  }
  return m;
};

// Counts for one feis, read once the organiser opens them.
export const useCompetitionCounts = (eventId, enabled) => {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    if (!eventId || !enabled) return undefined;
    let gone = false;
    serviceConsumer("GET", `${API}/feis/${eventId}/dancers`)
      .then((r) => !gone && setCounts(countsByCode(r?.result)))
      .catch(() => {});
    return () => {
      gone = true;
    };
  }, [eventId, enabled]);
  return counts;
};

// The signed-in family's dancers (for "Enter another dancer").
export const useFamilyDancers = (enabled = true) => {
  const [family, setFamily] = useState([]);
  useEffect(() => {
    if (!enabled) return undefined;
    let gone = false;
    serviceConsumer("GET", `${API}/account/dancers`)
      .then((r) => !gone && setFamily(r?.dancers || []))
      .catch(() => {});
    return () => {
      gone = true;
    };
  }, [enabled]);
  return family;
};
